import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  RefreshCw,
  Ban,
  Building2,
  Users,
  CheckCircle2,
  XCircle,
  Eye,
  Shield,
  Activity,
  FileCheck,
  Database,
  AlertOctagon,
  HeartPulse,
} from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CompanyDrillDown from '@/components/admin/CompanyDrillDown';
import { SuperAdminUserManagement } from '@/components/admin/SuperAdminUserManagement';
import { SuperAdminCompanyManagement } from '@/components/admin/SuperAdminCompanyManagement';
import { SuperAdminAuditLog } from '@/components/admin/SuperAdminAuditLog';
import { EvidenceCenter } from '@/components/admin/EvidenceCenter';
import { RecoveryCenter } from '@/components/admin/RecoveryCenter';
import { ExceptionCenter } from '@/components/admin/ExceptionCenter';
import { TenantHealthBoard } from '@/components/admin/TenantHealthBoard';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';

interface Company {
  id: string;
  name: string;
  owner_email: string;
  plan_id: string;
  plan_name: string;
  subscription_status: string;
  user_count: number;
  created_at: string;
}

interface User {
  id: string;
  user_id: string;
  email: string;
  role: string;
  company_name: string;
  created_at: string;
}

export default function SuperAdmin() {
  const { isSuperAdmin, loading: superAdminLoading } = useSuperAdmin();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCompany, setActiveCompany] = useState<string>('');
  const [drillDownCompany, setDrillDownCompany] = useState<{ id: string; name: string } | null>(
    null
  );
  const [suspendDialog, setSuspendDialog] = useState<{
    open: boolean;
    companyId: string;
    companyName: string;
  }>({
    open: false,
    companyId: '',
    companyName: '',
  });

  async function loadCompanies() {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke('super-list-companies', {});
    if (error) {
      toast.error(error.message);
      console.error('Error loading companies:', error);
    } else {
      setCompanies(data || []);
    }
    setLoading(false);
  }

  async function loadUsers(cid: string) {
    setActiveCompany(cid);
    const { data, error } = await supabase.functions.invoke('super-list-users', {
      body: { companyId: cid },
    });
    if (error) {
      toast.error(error.message);
      console.error('Error loading users:', error);
    } else {
      setUsers(data || []);
    }
  }

  async function suspendCompany(cid: string) {
    const { error } = await supabase.functions.invoke('super-suspend-company', {
      body: { companyId: cid, suspend: true },
    });
    if (error) {
      toast.error(error.message);
      console.error('Error suspending company:', error);
    } else {
      toast.success('Company suspended');
      loadCompanies();
    }
  }

  async function changePlan(cid: string, pid: string) {
    const { error } = await supabase.functions.invoke('super-update-subscription', {
      body: { companyId: cid, planId: pid },
    });
    if (error) {
      toast.error(error.message);
      console.error('Error changing plan:', error);
    } else {
      toast.success('Plan updated successfully');
      loadCompanies();
    }
  }

  useEffect(() => {
    loadCompanies();
  }, []);

  // Check super admin role
  if (superAdminLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  // Show drill-down if a company is selected
  if (drillDownCompany) {
    return (
      <div className="container mx-auto p-6">
        <CompanyDrillDown
          companyId={drillDownCompany.id}
          companyName={drillDownCompany.name}
          onBack={() => setDrillDownCompany(null)}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Super Admin Portal</h1>
          <p className="text-sm text-muted-foreground">
            Platform-wide administration and management
          </p>
        </div>
      </div>

      <Tabs defaultValue="subscriptions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="subscriptions" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Subscriptions
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            User Management
          </TabsTrigger>
          <TabsTrigger value="companies" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            All Companies
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Audit Log
          </TabsTrigger>
          <TabsTrigger value="evidence" className="flex items-center gap-2">
            <FileCheck className="h-4 w-4" />
            Evidence
          </TabsTrigger>
          <TabsTrigger value="recovery" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Recovery
          </TabsTrigger>
          <TabsTrigger value="exceptions" className="flex items-center gap-2">
            <AlertOctagon className="h-4 w-4" />
            Exceptions
          </TabsTrigger>
          <TabsTrigger value="health" className="flex items-center gap-2">
            <HeartPulse className="h-4 w-4" />
            Health
          </TabsTrigger>
        </TabsList>

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => (window.location.href = '/nda-management')}>
            <Shield className="h-4 w-4 mr-2" />
            NDA Management
          </Button>
        </div>

        <TabsContent value="subscriptions" className="space-y-4">
          <div className="flex items-center justify-end">
            <Button variant="outline" size="sm" onClick={loadCompanies}>
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>

          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Companies
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="p-8 flex justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead>Owner Email</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Users</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {companies.map((c) => (
                        <TableRow
                          key={c.id}
                          className={activeCompany === c.id ? 'bg-muted/50' : ''}
                        >
                          <TableCell className="font-medium">{c.name}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {c.owner_email || '—'}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={c.plan_id || 'LAUNCH'}
                              onValueChange={(v) => changePlan(c.id, v)}
                            >
                              <SelectTrigger className="w-[140px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="LAUNCH">Launch</SelectItem>
                                <SelectItem value="PRO">Pro</SelectItem>
                                <SelectItem value="GROWTH">Growth</SelectItem>
                                <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {c.subscription_status === 'active' ? (
                                <CheckCircle2 className="h-4 w-4 text-success" />
                              ) : (
                                <XCircle className="h-4 w-4 text-destructive" />
                              )}
                              <span className="capitalize">{c.subscription_status}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => loadUsers(c.id)}>
                              <Users className="h-4 w-4 mr-1" /> {c.user_count}
                            </Button>
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setDrillDownCompany({ id: c.id, name: c.name })}
                            >
                              <Eye className="h-4 w-4 mr-1" /> Details
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() =>
                                setSuspendDialog({
                                  open: true,
                                  companyId: c.id,
                                  companyName: c.name,
                                })
                              }
                              disabled={c.subscription_status === 'suspended'}
                            >
                              <Ban className="h-4 w-4 mr-1" /> Suspend
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {activeCompany && isSuperAdmin && (
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Users in {companies.find((x) => x.id === activeCompany)?.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">{u.email}</TableCell>
                          <TableCell>
                            <span className="capitalize">{u.role.replace('_', ' ')}</span>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {u.company_name || '—'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {new Date(u.created_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          {isSuperAdmin && <SuperAdminUserManagement />}
        </TabsContent>

        <TabsContent value="companies" className="space-y-4">
          {isSuperAdmin && <SuperAdminCompanyManagement />}
        </TabsContent>

        <TabsContent value="audit" className="space-y-4">
          {isSuperAdmin && <SuperAdminAuditLog />}
        </TabsContent>

        <TabsContent value="evidence" className="space-y-4">
          {isSuperAdmin && <EvidenceCenter />}
        </TabsContent>

        <TabsContent value="recovery" className="space-y-4">
          {isSuperAdmin && <RecoveryCenter />}
        </TabsContent>

        <TabsContent value="exceptions" className="space-y-4">
          {isSuperAdmin && <ExceptionCenter />}
        </TabsContent>

        <TabsContent value="health" className="space-y-4">
          {isSuperAdmin && <TenantHealthBoard />}
        </TabsContent>
      </Tabs>

      <AlertDialog
        open={suspendDialog.open}
        onOpenChange={(open) => setSuspendDialog({ ...suspendDialog, open })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend Company?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to suspend <strong>{suspendDialog.companyName}</strong>? This
              will immediately disable access for all users in this company.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                suspendCompany(suspendDialog.companyId);
                setSuspendDialog({ open: false, companyId: '', companyName: '' });
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Suspend
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
